import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from '_common/hooks/redux';
import { closeAndResetModal, updateModal } from '_common/modals/ModalsSlice';
import { useUploadTagsMutation } from 'Settings/pages/TenantSettingsPage/Tags/TagsApi';
import styles from './UploadTagsModal.module.scss';
import { Upload, UploadProps, Icon, ProgressBar, Modal, Button } from '@ets/design-system';

const MODAL = 'UploadTagsModal';

const UploadTagsModal = () => {
  // redux
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.modals.open[MODAL]);
  const state = useSelector((state) => state.modals[MODAL].state);
  const [mutUploadTags] = useUploadTagsMutation();

  // local
  const [file, setFile] = useState<File>();
  const [progress, setProgress] = useState<number>(0);

  const close = () => {
    dispatch(closeAndResetModal(MODAL));
  };

  const uploadDifferentFile = () => {
    dispatch(
      updateModal({
        modal: MODAL,
        data: {
          state: 'standby',
        },
      }),
    );
  };

  const handleFileUpload = (file: File, force: boolean) => {
    mutUploadTags({ file: file, force: force, onUploadProgress: setProgress });
  };

  const handleUpload: UploadProps['onDrop'] = async (acceptedFiles) => {
    mutUploadTags({ file: acceptedFiles[0], force: false, onUploadProgress: setProgress });
    setFile(acceptedFiles[0]);
  };

  const renderHeader = () => {
    switch (state) {
      case 'uploading':
      case 'standby':
        return <FormattedMessage id="UPLOAD_TAGS" />;
      case 'alreadyExist':
      case 'duplicated':
        return <FormattedMessage id="UPLOAD_TAGS_WARNING" />;
      case 'failed':
        return <FormattedMessage id="UPLOAD_TAGS_ERROR" />;
    }
  };

  const renderBody = () => {
    switch (state) {
      case 'standby':
        return (
          <Upload
            size="m"
            name="upload"
            icon="cloud-arrow-up"
            accept={{ 'text/csv': ['.csv'] }}
            onDrop={handleUpload} //false
            testId="upload-tags-input"
            className="w-full"
          >
            <FormattedMessage id="DRAG_AND_DROP_TAGS_FILE" />
          </Upload>
        );
      case 'uploading':
        return (
          <div className={styles.uploading}>
            <Icon icon="file" size="12" testId="uploading" />
            <FormattedMessage id="UPLOADING_YOUR_FILE" />
            <div style={{ marginTop: '3rem' }}>
              <ProgressBar
                size="m"
                variant="primary-light"
                progress={progress}
                testId="upload-tags"
              />
            </div>
          </div>
        );
      case 'duplicated':
        return <FormattedMessage id="IMPORTED_DUPLICATED_TAGS_WARNING" />;
      case 'alreadyExist':
        return <FormattedMessage id="IMPORT_TAGS_THAT_ALREADY_EXIST_WARNING" />;
      case 'failed':
        return <FormattedMessage id="IMPORTED_TAGS_ERROR" />;
    }
  };

  const renderFooter = () => {
    switch (state) {
      case 'duplicated':
        return (
          <>
            <Button
              variant="standard"
              size="m"
              onClick={uploadDifferentFile}
              testId="different-file"
            >
              <FormattedMessage id="UPLOAD_DIFFERENT_FILE" />
            </Button>
            <Button
              size="m"
              variant="primary"
              style={{ margin: '0 0 0 1rem' }}
              onClick={() => {
                file && handleFileUpload(file, true);
              }}
              testId="proceed"
            >
              <FormattedMessage id="PROCEED" />
            </Button>
          </>
        );
      case 'alreadyExist':
        return (
          <>
            <Button variant="standard" size="m" onClick={close} testId="go-back">
              <FormattedMessage id="GO_BACK" />
            </Button>
            <Button
              size="m"
              variant="primary"
              style={{ margin: '0 0 0 1rem' }}
              onClick={uploadDifferentFile}
              testId="different-file"
            >
              <FormattedMessage id="UPLOAD_DIFFERENT_FILE" />
            </Button>
          </>
        );
      case 'failed':
        return (
          <>
            <Button variant="standard" size="m" onClick={close} testId="go-back">
              <FormattedMessage id="GO_BACK" />
            </Button>
            <Button
              size="m"
              variant="primary"
              style={{ margin: '0 0 0 1rem' }}
              onClick={uploadDifferentFile}
              testId="try-again"
            >
              <FormattedMessage id="TRY_AGAIN" />
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Modal open={!!isOpen} close={close} width="68rem" testId="upload-tags">
      <Modal.Header>{renderHeader()}</Modal.Header>
      <Modal.Body>{renderBody()}</Modal.Body>
      <Modal.Footer>{renderFooter()}</Modal.Footer>
    </Modal>
  );
};

export default UploadTagsModal;
